import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, TextField, InputAdornment, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, LinearProgress
} from '@mui/material';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import { invoiceService, projectService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckIcon from '@mui/icons-material/Check';

const PAYMENT_METHODS = ['check', 'bank_transfer', 'zelle', 'venmo', 'credit_card', 'cash', 'wire'];

const methodIcons = {
  check: '🖊️', bank_transfer: '🏦', zelle: '⚡', venmo: '💙',
  credit_card: '💳', cash: '💵', wire: '🔗'
};

const statusConfig = {
  submitted: { color: '#fff3e0', text: '#e65100', label: 'Pending Review' },
  approved:  { color: '#e3f2fd', text: '#1565c0', label: 'Approved' },
  paid:      { color: '#e8f5e9', text: '#2e7d32', label: 'Paid' },
};

export default function Payments() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [search, setSearch] = useState('');
  const [payDialog, setPayDialog] = useState({ open: false, invoice: null });
  const [viewDialog, setViewDialog] = useState({ open: false, invoice: null });
  const [payForm, setPayForm] = useState({ payment_method: 'check', payment_reference: '', attachments: [] });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, projRes] = await Promise.all([
        invoiceService.getAll(),
        projectService.getAll()
      ]);
      setInvoices(invRes.data);
      setProjects(projRes.data);
    } catch (err) {}
  };

  const handleMarkPaid = async () => {
    if (!payDialog.invoice) return;
    setLoading(true);
    try {
      const payload = {
        payment_method: payForm.payment_method,
        payment_reference: payForm.payment_reference,
        attachments: payForm.attachments || []
      };
      console.log('Sending payment:', payDialog.invoice.id, payload);
      const res = await invoiceService.markPaid(payDialog.invoice.id, payload);
      console.log('Payment response:', res.data);
      setInvoices(prev => prev.map(i => i.id === payDialog.invoice.id
        ? { ...i, status: 'paid', payment_method: payForm.payment_method, payment_reference: payForm.payment_reference, attachments: JSON.stringify(payForm.attachments) }
        : i));
      setPayDialog({ open: false, invoice: null });
      setPayForm({ payment_method: 'check', payment_reference: '', attachments: [] });
      showAlert('Payment recorded successfully! Contractor has been notified.', 'success');
    } catch (err) {
      console.error('Payment error:', err);
      showAlert('Failed to record payment: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setLoading(false);
    }
    setLoading(false);
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 5000);
  };

  // Stats
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount), 0);
  const totalPending = invoices.filter(i => i.status === 'approved').reduce((s, i) => s + Number(i.amount), 0);
  const totalUnderReview = invoices.filter(i => i.status === 'submitted').reduce((s, i) => s + Number(i.amount), 0);
  const total = invoices.reduce((s, i) => s + Number(i.amount), 0);

  // Payment method breakdown
  const methodBreakdown = invoices.filter(i => i.status === 'paid').reduce((acc, i) => {
    const m = i.payment_method || 'other';
    acc[m] = (acc[m] || 0) + Number(i.amount);
    return acc;
  }, {});

  // Filter
  const filtered = invoices.filter(inv => {
    const matchesProject = selectedProject === 'all' || inv.project_id === selectedProject;
    const matchesSearch = inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      inv.milestone_title?.toLowerCase().includes(search.toLowerCase()) ||
      inv.first_name?.toLowerCase().includes(search.toLowerCase());
    return matchesProject && matchesSearch;
  });

  return (
    <Layout>
      {alert.show && <Alert severity={alert.severity} sx={{ mb: 3, borderRadius: 2 }}>{alert.message}</Alert>}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>Payments</Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>Track, manage and record all project payments</Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #2e7d32, #43a047)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalPaid.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Paid Out</Typography>
                </Box>
                <PaidIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
              <LinearProgress variant="determinate" value={total > 0 ? (totalPaid/total)*100 : 0}
                sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
              <Typography variant="caption" sx={{ opacity: 0.7 }}>{total > 0 ? ((totalPaid/total)*100).toFixed(0) : 0}% of total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalPending.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Ready to Pay</Typography>
                </Box>
                <AccountBalanceIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #e65100, #f57c00)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${totalUnderReview.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Under Review</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #6a1b9a, #8e24aa)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>${total.toLocaleString()}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Contract Value</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left: Project breakdown + Payment methods */}
        <Grid item xs={12} md={4}>
          {/* By Project */}
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6', mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e8eaf6' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>By Project</Typography>
            </Box>
            <Box>
              <Box sx={{ p: 1.5, cursor: 'pointer', bgcolor: selectedProject === 'all' ? '#e8eaf6' : 'white',
                borderBottom: '1px solid #f5f6fa', '&:hover': { bgcolor: '#f5f6fa' } }}
                onClick={() => setSelectedProject('all')}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>All Projects</Typography>
              </Box>
              {projects.map(proj => {
                const projInvoices = invoices.filter(i => i.project_id === proj.id);
                const paid = projInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.amount), 0);
                const pending = projInvoices.filter(i => i.status !== 'paid').reduce((s, i) => s + Number(i.amount), 0);
                const pct = (paid + pending) > 0 ? (paid / (paid + pending)) * 100 : 0;
                return (
                  <Box key={proj.id} onClick={() => setSelectedProject(proj.id)}
                    sx={{ p: 2, cursor: 'pointer', borderBottom: '1px solid #f5f6fa',
                      bgcolor: selectedProject === proj.id ? '#e8eaf6' : 'white',
                      '&:hover': { bgcolor: '#f5f6fa' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FolderIcon sx={{ fontSize: 16, color: '#1a237e' }} />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{proj.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#2e7d32' }}>Paid: ${paid.toLocaleString()}</Typography>
                      <Typography variant="caption" sx={{ color: '#e65100' }}>Due: ${pending.toLocaleString()}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{ borderRadius: 1, height: 6, bgcolor: '#ffccbc',
                        '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32', borderRadius: 1 } }} />
                  </Box>
                );
              })}
            </Box>
          </Card>

          {/* Payment Methods */}
          {Object.keys(methodBreakdown).length > 0 && (
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e8eaf6' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>Payment Methods</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {Object.entries(methodBreakdown).map(([method, amount]) => (
                  <Box key={method} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 20 }}>{methodIcons[method] || '💰'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                        {method.replace('_', ' ')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      ${amount.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}
        </Grid>

        {/* Right: Payment table */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e8eaf6' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e8eaf6' }}>
              <TextField fullWidth placeholder="Search by invoice, milestone, contractor..." value={search}
                onChange={e => setSearch(e.target.value)} size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999', fontSize: 18 }} /></InputAdornment> }} />
            </Box>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f6fa' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Contractor</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                        <ReceiptIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                        <Typography sx={{ color: '#999' }}>No payments found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map(inv => (
                    <TableRow key={inv.id} sx={{ '&:hover': { bgcolor: '#f5f6fa' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>{inv.invoice_number}</Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>{inv.milestone_title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: '#1a237e', width: 28, height: 28, fontSize: 12 }}>
                            {inv.first_name?.[0] || '?'}
                          </Avatar>
                          <Typography variant="body2">{inv.first_name} {inv.last_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>${Number(inv.amount).toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        {inv.payment_method ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 16 }}>{methodIcons[inv.payment_method] || '💰'}</Typography>
                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                              {inv.payment_method.replace('_', ' ')}
                            </Typography>
                          </Box>
                        ) : <Typography variant="caption" sx={{ color: '#ccc' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={statusConfig[inv.status]?.label || inv.status} size="small"
                          sx={{ bgcolor: statusConfig[inv.status]?.color, color: statusConfig[inv.status]?.text, fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => setViewDialog({ open: true, invoice: inv })}
                              sx={{ color: '#1a237e' }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {user?.role === 'homeowner' && inv.status === 'approved' && (
                            <Button size="small" variant="contained" startIcon={<CheckIcon />}
                              onClick={() => { setPayDialog({ open: true, invoice: inv }); setPayForm({ payment_method: 'check', payment_reference: '', attachments: [] }); }}
                              sx={{ bgcolor: '#2e7d32', borderRadius: 1, fontSize: 11, px: 1.5 }}>
                              Pay
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* View Details Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, invoice: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewDialog.invoice && (
          <>
            <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{viewDialog.invoice.invoice_number}</Typography>
                <Chip label={statusConfig[viewDialog.invoice.status]?.label} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Amount</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e' }}>
                    ${Number(viewDialog.invoice.amount).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Submitted</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {new Date(viewDialog.invoice.submitted_at).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Milestone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{viewDialog.invoice.milestone_title}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999' }}>Contractor</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{viewDialog.invoice.first_name} {viewDialog.invoice.last_name}</Typography>
                </Grid>
                {viewDialog.invoice.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#999' }}>Notes</Typography>
                    <Typography variant="body2">{viewDialog.invoice.notes}</Typography>
                  </Grid>
                )}
                {viewDialog.invoice.status === 'paid' && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#999' }}>Payment Method</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography>{methodIcons[viewDialog.invoice.payment_method] || '💰'}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                          {viewDialog.invoice.payment_method?.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#999' }}>Reference #</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{viewDialog.invoice.payment_reference || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#999' }}>Paid On</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{new Date(viewDialog.invoice.paid_at).toLocaleDateString()}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#999' }}>Proof of Payment</Typography>
                      {(() => {
                        let files = [];
                        try { 
                          const att = viewDialog.invoice.attachments;
                          if (Array.isArray(att)) files = att;
                          else if (typeof att === 'string') files = JSON.parse(att || '[]');
                        } catch(e) {}
                        return files.length > 0 ? (
                          <Box sx={{ mt: 1 }}>
                            {files.map((f, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8eaf6', borderRadius: 2, mb: 1 }}>
                                <Typography sx={{ fontSize: 20 }}>📎</Typography>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    <a href={f.url} target="_blank" rel="noreferrer" style={{ color: '#1a237e' }}>{f.name}</a>
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : <Typography variant="body2" sx={{ color: '#999' }}>No documents uploaded</Typography>;
                      })()}
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewDialog({ open: false, invoice: null })}>Close</Button>
              {user?.role === 'homeowner' && viewDialog.invoice.status === 'approved' && (
                <Button variant="contained" onClick={() => { setViewDialog({ open: false, invoice: null }); setPayDialog({ open: true, invoice: viewDialog.invoice }); }}
                  sx={{ bgcolor: '#2e7d32', borderRadius: 2 }}>
                  Record Payment
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={payDialog.open} onClose={() => setPayDialog({ open: false, invoice: null })}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #e8eaf6' }}>
          Record Payment
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {payDialog.invoice && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f6fa', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: '#999' }}>Recording payment for</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e' }}>
                ${Number(payDialog.invoice.amount).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>{payDialog.invoice.invoice_number} · {payDialog.invoice.first_name} {payDialog.invoice.last_name}</Typography>
            </Box>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Payment Method</InputLabel>
            <Select value={payForm.payment_method}
              onChange={e => setPayForm({...payForm, payment_method: e.target.value})}
              label="Payment Method" sx={{ borderRadius: 2 }}>
              {PAYMENT_METHODS.map(m => (
                <MenuItem key={m} value={m}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>{methodIcons[m]}</Typography>
                    <Typography sx={{ textTransform: 'capitalize' }}>{m.replace('_', ' ')}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField fullWidth label="Reference # (Check #, Transaction ID, etc.)"
            value={payForm.payment_reference}
            onChange={e => setPayForm({...payForm, payment_reference: e.target.value})}
            margin="normal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>
            Payment Confirmation Document
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
            Upload a scanned check, bank transfer receipt, or payment confirmation
          </Typography>
          <FileUpload
            onFilesChange={(files) => setPayForm({...payForm, attachments: files})}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, borderTop: '1px solid #e8eaf6' }}>
          <Button onClick={() => setPayDialog({ open: false, invoice: null })} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleMarkPaid} disabled={loading}
            startIcon={<PaidIcon />}
            sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3 }}>
            {loading ? 'Recording...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
